export type CardNetwork = 'visa' | 'mastercard' | 'amex' | 'other';

/**
 * Detect card network from the card number prefix (BIN).
 * - Visa: starts with 4
 * - Mastercard: starts with 51-55 or 2221-2720
 * - Amex: starts with 34 or 37
 * - Other: everything else
 */
export function detectCardNetwork(cardNumber: string): CardNetwork {
  const digits = cardNumber.replace(/\D/g, '');
  if (!digits) return 'other';

  if (digits.startsWith('4')) return 'visa';

  if (digits.startsWith('34') || digits.startsWith('37')) return 'amex';

  const twoDigit = parseInt(digits.substring(0, 2), 10);
  if (twoDigit >= 51 && twoDigit <= 55) return 'mastercard';

  const fourDigit = parseInt(digits.substring(0, 4), 10);
  if (fourDigit >= 2221 && fourDigit <= 2720) return 'mastercard';

  return 'other';
}

/**
 * Extract the last 4 digits from a card number string.
 */
export function getLastFour(cardNumber: string): string {
  const digits = cardNumber.replace(/\D/g, '');
  return digits.slice(-4);
}

/**
 * Luhn / mod-10 checksum on a digits-only string. Used by the
 * AddPaymentCardModal so we can flag an obviously-wrong number before
 * sending it to the server. Mirrors the BE check in
 * `dream-be/lib/cardValidation.ts`.
 */
export function luhnValid(digits: string): boolean {
  if (digits.length < 13 || digits.length > 19) return false;
  let sum = 0;
  let shouldDouble = false;
  for (let i = digits.length - 1; i >= 0; i--) {
    const d = digits.charCodeAt(i) - 48;
    if (d < 0 || d > 9) return false;
    let n = d;
    if (shouldDouble) {
      n *= 2;
      if (n > 9) n -= 9;
    }
    sum += n;
    shouldDouble = !shouldDouble;
  }
  return sum % 10 === 0;
}

/**
 * Parse a user-entered expiry string into {month, year (4-digit)}.
 * Accepts MM/YY, MM/YYYY, MM-YY, MMYY, MMYYYY (with or without
 * separator). Returns null when the shape doesn't match.
 */
export function parseExpiry(
  raw: string,
): { month: number; year: number } | null {
  const match = raw.trim().match(/^(\d{2})\s*[/-]?\s*(\d{2}|\d{4})$/);
  if (!match) return null;
  const month = parseInt(match[1], 10);
  let year = parseInt(match[2], 10);
  if (month < 1 || month > 12) return null;
  if (year < 100) year += 2000;
  if (year < 2000 || year > 2099) return null;
  return { month, year };
}

/**
 * Is the parsed expiry in the future (or current month)?
 */
export function expiryIsFuture(
  parsed: { month: number; year: number },
  now: Date = new Date(),
): boolean {
  const currentYear = now.getUTCFullYear();
  const currentMonth = now.getUTCMonth() + 1;
  if (parsed.year > currentYear) return true;
  if (parsed.year < currentYear) return false;
  return parsed.month >= currentMonth;
}
